// admin/ManageOrders.jsx
// View all orders and update their status (pending → confirmed → shipped → delivered).

import React, { useEffect, useState } from "react";
import {
  collection, getDocs, doc, updateDoc, orderBy, query,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import { formatCurrency } from "../../utils/pricing";
import { shortOrderId, formatDate, statusBadgeClass } from "../../utils/helpers";

const STATUS_FLOW = ["pending", "confirmed", "shipped", "delivered", "cancelled"];

export default function ManageOrders() {
  const [orders, setOrders]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filterStatus, setFilter] = useState("All");
  const [search, setSearch]       = useState("");
  const [expanded, setExpanded]   = useState(null); // order id whose details are open

  async function fetchOrders() {
    setLoading(true);
    const snap = await getDocs(query(collection(db, "orders"), orderBy("createdAt", "desc")));
    setOrders(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setLoading(false);
  }

  useEffect(() => { fetchOrders(); }, []);

  // Update order status in Firestore and local state
  async function handleStatusChange(orderId, newStatus) {
    await updateDoc(doc(db, "orders", orderId), { status: newStatus });
    setOrders((prev) =>
      prev.map((o) => (o.id === orderId ? { ...o, status: newStatus } : o))
    );
  }

  // Filtered list
  const filtered = orders.filter((o) => {
    const matchStatus = filterStatus === "All" || o.status === filterStatus;
    const matchSearch =
      !search ||
      shortOrderId(o.id).toLowerCase().includes(search.toLowerCase()) ||
      (o.shipping?.name || "").toLowerCase().includes(search.toLowerCase()) ||
      (o.userEmail || "").toLowerCase().includes(search.toLowerCase());
    return matchStatus && matchSearch;
  });

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-extrabold text-gray-900">Orders</h1>
        <p className="text-sm text-gray-500">{orders.length} total orders</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <input
          type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by order ID, name, email…"
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 w-64"
        />
        <select value={filterStatus} onChange={(e) => setFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white">
          <option value="All">All Statuses</option>
          {STATUS_FLOW.map((s) => (
            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400 animate-pulse">Loading orders…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-gray-400">No orders found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                <tr>
                  <th className="text-left px-5 py-3 w-8" />
                  <th className="text-left px-5 py-3">Order ID</th>
                  <th className="text-left px-5 py-3">Customer</th>
                  <th className="text-left px-5 py-3">Date</th>
                  <th className="text-right px-5 py-3">Total</th>
                  <th className="text-left px-5 py-3">Status</th>
                  <th className="text-left px-5 py-3">Update Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((order) => (
                  <React.Fragment key={order.id}>
                    <tr className="hover:bg-gray-50 transition-colors">
                      {/* Expand toggle */}
                      <td className="px-3 py-3">
                        <button
                          onClick={() => setExpanded(expanded === order.id ? null : order.id)}
                          className="text-gray-400 hover:text-purple-600 text-base w-6 text-center"
                        >
                          {expanded === order.id ? "▲" : "▼"}
                        </button>
                      </td>
                      <td className="px-5 py-3 font-mono font-semibold text-gray-700">{shortOrderId(order.id)}</td>
                      <td className="px-5 py-3">
                        <p className="font-medium text-gray-800">{order.shipping?.name || "—"}</p>
                        <p className="text-xs text-gray-400">{order.userEmail}</p>
                      </td>
                      <td className="px-5 py-3 text-gray-500">{formatDate(order.createdAt)}</td>
                      <td className="px-5 py-3 text-right font-semibold text-gray-800">{formatCurrency(order.pricing?.total)}</td>
                      <td className="px-5 py-3">
                        <span className={`capitalize text-xs font-semibold px-2.5 py-1 rounded-full ${statusBadgeClass(order.status)}`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        {/* Status dropdown – only show valid next statuses */}
                        <select
                          value={order.status}
                          onChange={(e) => handleStatusChange(order.id, e.target.value)}
                          className="border border-gray-300 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                        >
                          {STATUS_FLOW.map((s) => (
                            <option key={s} value={s}>
                              {s.charAt(0).toUpperCase() + s.slice(1)}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>

                    {/* Expanded order details */}
                    {expanded === order.id && (
                      <tr>
                        <td colSpan={7} className="px-5 pb-4 bg-purple-50">
                          <div className="py-3">
                            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Items ordered</p>
                            <div className="space-y-2">
                              {order.items?.map((item, i) => (
                                <div key={i} className="flex items-center gap-3 bg-white rounded-xl p-3 shadow-sm">
                                  <img src={item.image} alt={item.name}
                                    className="w-10 h-10 rounded-lg object-cover bg-gray-100"
                                    onError={(e) => { e.target.src = "https://via.placeholder.com/40?text=IMG"; }}
                                  />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-800 truncate">{item.name}</p>
                                    <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                                  </div>
                                  <span className="text-sm font-semibold text-gray-700">
                                    {formatCurrency(item.price * item.quantity)}
                                  </span>
                                </div>
                              ))}
                            </div>

                            <div className="mt-3 grid grid-cols-2 gap-3 text-xs text-gray-600">
                              <div>
                                <p className="font-semibold text-gray-700 mb-1">Shipping to</p>
                                <p>{order.shipping?.address}</p>
                                <p>{order.shipping?.city}, {order.shipping?.state} {order.shipping?.zip}</p>
                                <p>{order.shipping?.country}</p>
                              </div>
                                <div>
                                  <p className="font-semibold text-gray-700 mb-1">Pricing</p>
                                  <p>Subtotal: {formatCurrency(order.pricing?.subtotal)}</p>
                                  <p>Shipping: {formatCurrency(order.pricing?.shippingCost)}</p>
                                  <p>Tax: {formatCurrency(order.pricing?.tax)}</p>
                                  {order.isCouponApplied && (
                                    <>
                                      <p className="text-gray-400 mt-1">Original Total: <span className="line-through">{formatCurrency(order.pricing?.total + (order.pricing?.discount || 0))}</span></p>
                                      <div className="mt-2 p-2 bg-green-50 border border-green-100 rounded-lg">
                                        <p className="text-green-800 font-bold flex justify-between">
                                          <span>Coupon: {order.coupon?.couponCode}</span>
                                          <span className="text-[10px] bg-green-200 px-1 rounded uppercase">{order.coupon?.discountType}</span>
                                        </p>
                                        <p className="text-green-700">Value: {order.coupon?.discountType === 'percentage' ? `${order.coupon?.discountValue}%` : formatCurrency(order.coupon?.discountValue)}</p>
                                        <p className="text-green-800 font-bold">Discount: -{formatCurrency(order.coupon?.discountAmount)}</p>
                                      </div>
                                    </>
                                  )}
                                  <p className="font-semibold mt-1 text-lg">Total: {formatCurrency(order.pricing?.total)}</p>
                                </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
